import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Avatar,
  useColorModeValue
} from '@chakra-ui/react';
import { RiMailSendLine } from 'react-icons/ri';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);
const MotionText = motion(Text);

const TypewriterText = ({ text, delay = 0, onComplete = () => {} }) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showCursor, setShowCursor] = useState(true);
  const textColor = useColorModeValue('blue.500', 'inherit');

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, 50);
      return () => clearTimeout(timeout);
    } else {
      onComplete();
    }
  }, [currentIndex, text, onComplete]);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <MotionText
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay, duration: 0.5 }}
      color={text.startsWith('I notice') || text.startsWith('Looking') || text.startsWith('Your') ? textColor : 'inherit'}
      fontFamily="mono"
    >
      {displayText}
      {currentIndex < text.length && (
        <Box
          as="span"
          opacity={showCursor ? 1 : 0}
          transition="opacity 0.2s"
        >
          |
        </Box>
      )}
    </MotionText>
  );
};

const conversations = [
  {
    user: "Work was hectic today, so I had to skip the gym...",
    agent: "Looking at your pattern, you've hit the gym 12 times this month — your best streak yet! One missed day doesn't break your momentum.",
  },
  {
    user: "This client is so demanding. I feel like giving up on this project.",
    agent: "I notice you've successfully handled 3 similar challenges before. Remember how you turned the SharePoint project around?",
  },
  {
    user: "My presentation didn't go well today. I stumbled over my words.",
    agent: "Your communication has improved 40% since January. Every presentation, even the tough ones, builds your confidence.",
  }
];

export const EmailAnimation = () => {
  const messagesEndRef = React.useRef(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  
  };
  const [conversationIndex, setConversationIndex] = useState(0);
  const [showUserMessage, setShowUserMessage] = useState(true);
  const [showAgentMessage, setShowAgentMessage] = useState(false);
  
  const bgColor = useColorModeValue('gray.100', 'gray.700');
  const headerBgColor = useColorModeValue('gray.200', 'gray.600');
  const messageBgColor = useColorModeValue('blue.50', 'blue.900');

  const startNextConversation = () => {
    setShowUserMessage(false);
    setShowAgentMessage(false);
    setTimeout(() => {
      setConversationIndex((prev) => (prev + 1) % conversations.length);
      setShowUserMessage(true);
      scrollToBottom();
    }, 1000);
  };

  const onUserMessageComplete = () => {
    setTimeout(() => {
      setShowAgentMessage(true);
      scrollToBottom();
    }, 500);
  };

  const onAgentMessageComplete = () => {
    setTimeout(startNextConversation, 3000);
  };

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      width={{ base: '100%', md: '500px' }}
      maxWidth="100%"
      position="relative"
      height={{ base: '350px', md: '400px' }}
      mx="auto"
      zIndex={1}
      display="block"
      visibility="visible"
    >
      <Box
        position="relative"
        width="full"
        height="full"
        minHeight={{ base: '350px', md: '400px' }}
        bg={bgColor}
        borderRadius="xl"
        overflow="hidden"
        shadow="2xl"
      >
        <VStack height="full" spacing={0}>
          <HStack
            w="full"
            p={4}
            bg={headerBgColor}
            spacing={4}
          >
            <Icon as={RiMailSendLine} w={6} h={6} color="blue.500" />
            <Text fontWeight="bold">Email Conversation</Text>
          </HStack>
          <Box 
            p={6} 
            w="full" 
            flex={1} 
            overflowY="auto" 
            maxHeight="300px"
            css={{
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'gray.200',
                borderRadius: '24px',
              },
            }}
            ref={messagesEndRef}
          >
            {showUserMessage && (
              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                mb={4}
              >
                <HStack mb={2} spacing={2}>
                  <Avatar size="sm" name="User" bg="blue.500" />
                  <Text fontWeight="medium">you@email.com</Text>
                </HStack>
                <TypewriterText 
                  text={conversations[conversationIndex].user}
                  onComplete={onUserMessageComplete}
                />
              </MotionBox>
            )}
            {showAgentMessage && (
              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                p={4}
                bg={messageBgColor}
                borderRadius="md"
                borderLeftWidth={4}
                borderLeftColor="blue.500"
              >
                <HStack mb={2} spacing={2}>
                  <Avatar size="sm" name="AI Agent" src="/ai-avatar.png" />
                  <Text fontWeight="medium">agent@emailed.app</Text>
                </HStack>
                <TypewriterText 
                  text={conversations[conversationIndex].agent}
                  onComplete={onAgentMessageComplete}
                />

              </MotionBox>
            )}
          </Box>
        </VStack>
      </Box>
    </MotionBox>
  );
};
